import { useCallback, useEffect, useState, type MouseEvent } from "react";
import { createPieceLink } from "../../../components/catalogue/catalogueResellerApi";
import { askAboutPieceMessage, whatsAppHref } from "./catalogueAudience";

/** The share link a viewer is on, and the piece id they use (an alias on client links). */
export type PieceLinkContext = {
  catalogueId?: string;
  linkToken?: string;
  sessionToken?: string;
  pieceId?: string;
};

const SHORT_LINK_WAIT_MS = 4000;
const codes = new Map<string, Promise<string | null>>();

/** One short-link request per piece and link; a failed one may be tried again. */
export function pieceShortCode(ctx: PieceLinkContext): Promise<string | null> {
  const { catalogueId, linkToken, sessionToken, pieceId } = ctx;
  if (!catalogueId || !linkToken || !sessionToken || !pieceId) return Promise.resolve(null);
  const key = `${catalogueId}|${linkToken}|${pieceId}`;
  let pending = codes.get(key);
  if (!pending) {
    const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), SHORT_LINK_WAIT_MS));
    pending = Promise.race([createPieceLink(catalogueId, linkToken, sessionToken, pieceId), timeout]).then((code) => {
      if (!code) codes.delete(key);
      return code;
    });
    codes.set(key, pending);
  }
  return pending;
}

export function shortPieceUrl(code: string, origin = window.location.origin): string {
  return `${origin}/w/${code}`;
}

/** Message with the short link when there is one (its preview shows the photo), else the long link and photo. */
export function askMessage(pieceName: string, code: string | null, fallback: { pieceUrl?: string; photoUrl?: string }): string {
  return code ? askAboutPieceMessage(pieceName, { pieceUrl: shortPieceUrl(code) }) : askAboutPieceMessage(pieceName, fallback);
}

/**
 * "Ask on WhatsApp" for one piece. The link carries the short /w/<code> URL once it is known.
 * `eager` fetches it up front (a piece page); otherwise call `warm` on hover or touch. A tap that
 * comes before the code is ready opens the tab at once and fills it in when the code arrives.
 */
export function useAskOnWhatsApp(opts: {
  phone?: string | null;
  pieceName: string;
  ctx: PieceLinkContext;
  fallback: { pieceUrl?: string; photoUrl?: string };
  eager?: boolean;
}) {
  const { phone, pieceName, ctx, fallback, eager } = opts;
  const { catalogueId, linkToken, sessionToken, pieceId } = ctx;
  const canShorten = Boolean(catalogueId && linkToken && sessionToken && pieceId);
  const [code, setCode] = useState<string | null>(null);

  const warm = useCallback(() => {
    if (!canShorten) return;
    void pieceShortCode({ catalogueId, linkToken, sessionToken, pieceId }).then((c) => {
      if (c) setCode(c);
    });
  }, [canShorten, catalogueId, linkToken, sessionToken, pieceId]);

  useEffect(() => {
    setCode(null);
    if (eager) warm();
  }, [eager, warm]);

  const href = whatsAppHref(phone, askMessage(pieceName, code, fallback));

  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (code || !canShorten) return;
    event.preventDefault();
    const tab = window.open("", "_blank");
    void pieceShortCode({ catalogueId, linkToken, sessionToken, pieceId }).then((c) => {
      if (c) setCode(c);
      const target = whatsAppHref(phone, askMessage(pieceName, c, fallback));
      if (tab) {
        tab.opener = null;
        tab.location.href = target;
      } else {
        window.location.assign(target);
      }
    });
  };

  return { href, onClick, warm };
}
